import { Button, Stack, Text } from '@mantine/core';
import { NavLink, useSearchParams } from 'react-router-dom';
import { GENERAL_NAVS, IDENTITY_NAVS, profilePath } from '../../lib/profileNav';
import type { ProfileNavItem } from '../../lib/profileNav';
import { IconChevronLeft } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { isOrgAdmin } from '../../lib/permissions';
import { backLinkFor } from '../../lib/productBackLink';
import { useOpenRequestCount } from '../../hooks/useOpenRequestCount';
import { BadgeBlock } from '../../components/BadgeBlock';

const SECTIONS = [
  { group: undefined, items: GENERAL_NAVS },
  { group: 'Identity & Access', items: IDENTITY_NAVS },
];

const STYLES = {
  '--button-color': 'var(--text-muted)',
  '--button-hover': 'var(--bg-subtle)',
  '--button-hover-color': 'var(--text-default)',
}


function SidebarItem({ item, search = '', count }: Readonly<{ item: ProfileNavItem; search?: string; count?: number | null }>) {
  const { Icon } = item;
  return (
    <Button component={NavLink}
      to={{ pathname: profilePath(item.segment), search }}
      className='profile-nav'
      variant='subtle' w={'100%'} px={12} fz={'sm'} h={'2.25rem'} display={'flex'} justify='flex-start' leftSection={Icon && <Icon size={16} style={{ flexShrink: 0 }} />}
      rightSection={count ? (
        <BadgeBlock color="var(--accent-text)" bg="var(--brand-soft)" fz={11} fw={700} tt="none" bdrs={6} py={0} h="auto">
          {count}
        </BadgeBlock>
      ) : undefined}
      styles={{ label: { flex: 1, textAlign: 'left' } }}
      style={STYLES}
    >
      {item.label}
    </Button>
  );
}

export function ProfileSidebar() {
  const { permissions } = useAuth();
  const [searchParams] = useSearchParams();
  // A user who arrived from a product leaves the same way; the Hub stays one click away in the top nav.
  const backTo = backLinkFor(searchParams.get('from'));
  // Re-selecting My account must not drop the product it was reached from.
  const keepFrom = backTo ? `?from=${encodeURIComponent(searchParams.get('from') ?? '')}` : '';
  // Every section but "My account" reads organisation-scoped data the API refuses a plain
  // member, so a member is shown the one page that works rather than a row of 403s.
  const orgAdmin = isOrgAdmin(permissions);
  const openRequests = useOpenRequestCount();
  const counts: Record<NonNullable<ProfileNavItem['countOf']>, number | null> = { openRequests };
  const sections = SECTIONS
    .map(({ group, items }) => ({ group, items: items.filter((item) => orgAdmin || !item.orgAdminOnly) }))
    .filter(({ items }) => items.length > 0);

  return (
    <Stack
      component="aside"
      p={'18px 12px'}
      bg={'var(--bg-surface)'}
      gap={4}
      w={232}
      style={{
        flexShrink: 0,
        borderRight: '1px solid var(--border-default)',
      }}
    >
      {backTo ? (
        <Button component="a" href={backTo.path} mb={8} variant='subtle' w={'100%'} px={12} fz={'xs'} h={'2rem'} display={'flex'} justify='flex-start' leftSection={<IconChevronLeft size={16} />}
          style={STYLES}
        >
          Back to {backTo.name}
        </Button>
      ) : (
      <Button component={NavLink} to={'/'} mb={8} variant='subtle' w={'100%'} px={12} fz={'xs'} h={'2rem'} display={'flex'} justify='flex-start' leftSection={<IconChevronLeft size={16} />}
        style={STYLES}
      >
        Back to Hub
      </Button>
      )}

      {sections.map(({ group, items }) => (
        <Stack key={group ?? '__root__'} gap={4}>
          {group && <Text fz={10} c={'var(--text-subtle)'} tt={'uppercase'} p={'12px 12px 6px'} fw={700} lts={'0.6px'} >
            {group}
          </Text>}
          <Stack gap={2}>
            {items.map((item) => (
              <SidebarItem key={item.segment} item={item} search={item.segment === 'me' ? keepFrom : ''}
                count={item.countOf ? counts[item.countOf] : undefined} />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
